import {Injectable} from "@angular/core";
import {AbstractControl, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {DataResourceModel, EntityData, ValidationService} from "@fedeltapointofsale/data-client";
import {debounceTime, Observable, share, Subject, switchMap, take} from "rxjs";
import {EntityFormControl} from "./entity-form-control";
import {EntityFormGroup} from "./entity-form-group";
import {EntityFormCollectionControl} from "./entity-form-collection-control";

/**
 * Form builder that injects entity validator for the fields.
 * It uses the form control name in snake case format to define the nested path. i.e. primaryCategory_categoryId
 */
@Injectable()
export class EntityFormBuilder {

    EXCLUDED_FIELDS: string[] = ["localisableContext", "localisableChildContext", "version"];

    constructor(private formBuilder: FormBuilder, private validationService: ValidationService) {
    }

    /**
     * Construct a new {@link FormGroup} with the given map of configuration injecting the entity validator.
     * Valid keys for the `extra` parameter map are `validator` and `asyncValidator`.
     *
     * See the {@link FormGroup} constructor for more details.
     */
    group(entityModel: DataResourceModel<any>, controlsConfig?: { [key: string]: any }, extra?: {
        [key: string]: any
    }): FormGroup {
        if (controlsConfig) {
            return this.resolveControlConfig(entityModel, entityModel.data, undefined, undefined, controlsConfig, extra);
        } else {
            return this.createFormGroupForEntity(entityModel, entityModel.data);
        }
    }

    groupCollection(entityModel: DataResourceModel<any>, path: string, rowIndex: number, formColumns?: string[], controlsConfig?: {
        [key: string]: any
    }, extra?: { [key: string]: any }): FormGroup {
        if (controlsConfig) {
            const entity: EntityData<any> = entityModel.data.createChildInstance(path);
            return this.resolveControlConfig(entityModel, entity, path, rowIndex, controlsConfig, extra);
        } else if (entityModel.data.getEntityDataType(path)) {
            const entity: EntityData<any> = entityModel.data.createChildInstance(path);
            return this.createFormGroupForEntity(entityModel, entity, formColumns, path, rowIndex);
        } else {
            return this.createFormGroupForPrimitive(entityModel, path, rowIndex);
        }
    }

    subGroup(controlsConfig: { [key: string]: any }, extra?: { [key: string]: any }): FormGroup {
        return this.formBuilder.group(controlsConfig, extra);
    }

    private resolveControlConfig(entityModel: DataResourceModel<any>, entity: EntityData<any>, path: string, rowIndex: number, controlsConfig: {
        [key: string]: any
    }, extra?: { [key: string]: any }): FormGroup {
        for (const key in controlsConfig) {
            if (controlsConfig.hasOwnProperty(key)) {
                if (controlsConfig[key] instanceof FormGroup) {
                    this.resolveFormGroup(entityModel, entity[key], controlsConfig[key], path);
                } else {
                    const value: Array<any> = controlsConfig[key];

                    const validationKey: string = (entity.getPathKey(key) || key).replace("_", ".");
                    const fieldPath: string = path ? path + "." + validationKey : validationKey;

                    const validator = new AsyncValidator(this.validationService, entityModel, fieldPath, entity.getIdentifierKey(), rowIndex).validate;

                    if (value.length <= 2) {
                        if (value.length === 1) {
                            value.push(null);
                        }
                        value.push(validator);
                    } else {
                        value[2] = Validators.composeAsync([value[2], validator]);
                    }
                }
            }
        }
        return this.formBuilder.group(controlsConfig, extra);
    }

    createFormGroupForEntity(entityModel: DataResourceModel<any>, entity: EntityData<any>, formColumns?: string[], path?: string, rowIndex?: number): FormGroup {
        const controls: { [key: string]: AbstractControl } = {};

        const fields: string[] = entity.getFields().slice(0);
        // add the identifier to the list in case the id is a transientIdentifier
        if (entity.getIdentifierKey() && fields.find((field) => field === entity.getIdentifierKey()) == undefined) {
            fields.push(entity.getIdentifierKey());
        }

        fields.forEach((key) => {
            if (!this.EXCLUDED_FIELDS.find(value => value === key)) {
                const validationKey: string = entity.getPathKey(key) || key;
                const fieldPath: string = path ? path + "." + validationKey : validationKey;
                if (formColumns && formColumns.indexOf(fieldPath) == -1) {
                    return;
                }
                const validator = new AsyncValidator(this.validationService, entityModel, fieldPath, entity.getIdentifierKey(), rowIndex).validate;
                if (entity.isEntityDataArray(key)) {
                    const childValidator = new ChildrenValidator().validate;
                    controls[key] = new EntityFormCollectionControl(fieldPath, null, childValidator, validator);
                } else if (entity.getEntityDataType(key)) {
                    controls[key] = this.createFormGroupForEntity(entityModel, entity[key], formColumns, fieldPath, rowIndex);
                } else {
                    controls[key] = new EntityFormControl(fieldPath, null, null, validator);
                    this.validationService.isFieldRequired(entityModel.data, fieldPath).then((required) => (<EntityFormControl>controls[key]).required = required);
                    this.validationService.hasAnyValidation(entityModel.data, fieldPath).then((hasAnyValidation) => (<EntityFormControl>controls[key]).validators = hasAnyValidation);
                }
            }
        });

        return new EntityFormGroup(path, controls);
    }

    private createFormGroupForPrimitive(entityModel: DataResourceModel<any>, path?: string, rowIndex?: number): FormGroup {
        const controls: { [key: string]: AbstractControl } = {};

        const key = "value";
        const fieldPath: string = path + "." + key;
        const validator = new AsyncValidator(this.validationService, entityModel, fieldPath, undefined, rowIndex).validate;
        controls[key] = new EntityFormControl(fieldPath, null, null, validator);
        this.validationService.isFieldRequired(entityModel.data, fieldPath).then((required) => (<EntityFormControl>controls[key]).required = required);
        this.validationService.hasAnyValidation(entityModel.data, fieldPath).then((hasAnyValidation) => (<EntityFormControl>controls[key]).validators = hasAnyValidation);

        return new EntityFormGroup(path, controls);
    }

    private resolveFormGroup(entityModel: DataResourceModel<any>, entity: EntityData<any>, formGroup: FormGroup, path: string): void {
        for (const key in formGroup.controls) {
            const formControl: AbstractControl = formGroup.controls[key];
            const validationKey: string = entity.getPathKey(key) || key;
            const fieldPath: string = path ? path + "." + validationKey : validationKey;
            if (formControl instanceof FormGroup) {
                this.resolveFormGroup(entityModel, entity[key], formControl, fieldPath);
            } else {
                const validator = new AsyncValidator(this.validationService, entityModel, entity.getIdentifierKey(), fieldPath).validate;
                formControl.asyncValidator = formControl.asyncValidator == null ? validator : Validators.composeAsync([formControl.asyncValidator, validator]);
            }
        }
    }

    markControlsAsTouched(formGroup: FormGroup): void {
        for (const key in formGroup.controls) {
            if (formGroup.controls[key] instanceof FormGroup) {
                this.markControlsAsTouched(<FormGroup>formGroup.controls[key]);
            } else {
                formGroup.controls[key].markAsTouched({onlySelf: true});
                formGroup.controls[key].updateValueAndValidity({onlySelf: true, emitEvent: true});
            }
        }
    }

    customGroup(controlsConfig?: { [key: string]: any }, extra?: { [key: string]: any }): FormGroup {
        const controls: { [key: string]: AbstractControl } = {};
        for (const key in controlsConfig) {
            if (controlsConfig.hasOwnProperty(key)) {
                const value: Array<any> = controlsConfig[key];
                controls[key] = new EntityFormControl(null, null, value[1], null);
                controls[key]["validators"] = true;
            }
        }
        return new FormGroup(controls);
    }
}

class AsyncValidator {
    validatorInput: Subject<any>;
    validatorChain: Observable<any>;
    control: AbstractControl;
    identifierKey: string;
    rowIndex: number;
    validate = (control: AbstractControl) => {
        this.control = control;
        let identifier: any = undefined;
        if (this.identifierKey && this.control.parent) {
            identifier = this.control.parent.get(this.identifierKey).value;
        }
        // An immediate timeout is set because the next has to occur after the
        // validator chain is subscribed to.
        setTimeout(() => this.validatorInput.next({identifier: identifier, value: control.value}), 0);

        return this.validatorChain;
    }

    constructor(private validationService: ValidationService, private entityModel: DataResourceModel<any>, private fieldPath: string, identifierKey: string, rowIndex?: number) {
        this.validatorInput = new Subject<any>();
        this.identifierKey = identifierKey;
        this.rowIndex = rowIndex;

        //noinspection TypeScriptValidateTypes
        this.validatorChain = this.validatorInput.pipe(
            debounceTime(350),
            switchMap((field: { identifier: any, value: any }) => {
                return validationService.validateField(entityModel.data, field.value, fieldPath, rowIndex, field.identifier);
            }),
            share(),
            take(1)
        );
    }
}

class ChildrenValidator {
    validate = (control: AbstractControl): { [key: string]: any } => {
        if (control.value && control.value.length && control.value.find((item) => item && item[EntityFormCollectionControl.FLAG_CHILD_ERROR]) !== undefined) {
            return {"childInError": true};
        } else {
            return null;
        }
    }
}
